import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchWithcookieData,
  selectCompanyIsUse,
  fetchPopupPosts,
  selectPopups,
  selectPopupsLoading,
  selectWithcookieData,
  fetchCartItems,
  selectIsUserLoggedIn,
  selectIsAdminLoggedIn,
  selectUserInfo
} from './redux/WithcookieSlice';
import { useTranslation } from './redux/TranslationHelper';
import ComponentRendererWithProps from './components/ComponentRendererWithProps';
import { CartComponent } from './components/module/Cart/index';
import { PaymentComponent } from './components/module/Payment';
import { PaymentSuccessComponent as PaymentSuccess } from './components/module/PaymentSuccess';
import { PaymentFailComponent as PaymentFail } from './components/module/PaymentFail';
import { ProductDetailComponent } from './components/module/ProductDetail';
import { getLayout } from './layouts/LayoutRegistry';
// 🚀 finapp을 직접 import하지 않고 lazy loading 사용
// import FinappApp from './components/finapp';
import './App.css';

console.error('🚨 Preview.tsx 파일이 로드됨 - ERROR 레벨');




// ------------------------------------------------------------------ 팝업 관련 내용 시작
  // 팝업 컴포넌트 정의
  interface PopupProps {
    id: string;
    title: string;
    content: string;
    links: {
      imgUrl: string;
      textAlignment: string;
      textJustify: string;
      textColor: string;
      backgroundColor: string;
      popupPadding: string;
      link: string;
    };
    onClose: (popupId: string) => void;
    onNeverShow: (popupId: string) => void;
    currentIndex?: number;
    totalCount?: number;
    onNext?: () => void;
    onPrev?: () => void;
  }
  // 팝업 컴포넌트
  const Popup: React.FC<PopupProps> = ({
    id,
    title,
    content,
    links,
    onClose,
    onNeverShow,
    currentIndex = 0,
    totalCount = 1,
    onNext,
    onPrev
  }) => {
    const { t } = useTranslation();
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    // 스와이프 감지를 위한 최소 거리
    const minSwipeDistance = 50;

    // 터치 이벤트 핸들러
    const onTouchStart = (e: React.TouchEvent) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
      setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
      if (!touchStart || !touchEnd) return;

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > minSwipeDistance;
      const isRightSwipe = distance < -minSwipeDistance;

      if (isLeftSwipe && onNext) {
        onNext();
      }
      if (isRightSwipe && onPrev) {
        onPrev();
      }
    };

    // 마우스 이벤트 핸들러 (데스크톱 지원)
    const [mouseStart, setMouseStart] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const onMouseDown = (e: React.MouseEvent) => {
      setMouseStart(e.clientX);
      setIsDragging(true);
    };

    const onMouseMove = (e: React.MouseEvent) => {
      if (!isDragging) return;
      if (e.clientX) {
        const distance = mouseStart! - e.clientX;
        if (Math.abs(distance) > minSwipeDistance) {
          if (distance > 0 && onNext) {
            onNext();
            setIsDragging(false);
          }
          if (distance < 0 && onPrev) {
            onPrev();
            setIsDragging(false);
          }
        }
      }
    };

    const onMouseUp = () => {
      setIsDragging(false);
    };

    const handleLinkClick = () => {
      if (links.link) {
        window.open(links.link, '_blank');
      }
    };

    const popupStyle = {
      position: 'fixed' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1000,
      backgroundColor: links.backgroundColor || '#ffffff',
      padding: `${links.popupPadding || 20}px`,
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      maxWidth: '90vw',
      maxHeight: '90vh',
      overflow: 'auto',
      minWidth: '270px',
      display: 'flex',
      flexDirection: 'column' as const,
      color: links.textColor || '#000000',
    };

    const contentStyle = {
      textAlign: links.textAlignment as any || 'left',
      justifyContent: links.textJustify || 'flex-start',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: links.textAlignment || 'flex-start',
    };

    const overlayStyle = {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
    };

    const closeButtonStyle = {
      position: 'absolute' as const,
      top: '0',
      right: '0',
      background: links.backgroundColor || '#ffffff',
      border: 'none',
      borderRadius: '0 8px 0 8px', // 오른쪽 상단 모서리에만 둥근 모서리 적용
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      cursor: 'pointer',
      color: links.textColor || '#000000',
      margin: '0',
      padding: '0',
      zIndex: 1001,
    };

    const imageStyle = {
      maxWidth: '100%',
      width: '100%',
      height: 'auto',
      cursor: links.link ? 'pointer' : 'default',
      marginBottom: '15px',
    };

    const footerStyle = {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '15px',
      paddingTop: '10px',
      borderTop: '1px solid #eee',
    };

    const buttonStyle = {
      background: 'none',
      border: 'none',
      color: '#4361ee',
      cursor: 'pointer',
      padding: '5px 10px',
      fontSize: '14px',
      textDecoration: 'underline',
    };

    const paginationStyle = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: '15px',
    };

    const dotStyle = (isActive: boolean) => ({
      width: isActive ? '14px' : '10px',
      height: isActive ? '14px' : '10px',
      borderRadius: '50%',
      backgroundColor: isActive ? '#4361ee' : '#ccc',
      margin: '0 5px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      opacity: isActive ? 1 : 0.6,
    });

    // 스와이프 이벤트를 위한 컨테이너 스타일
    const swipeContainerStyle = {
      position: 'relative' as const,
      width: '100%',
      userSelect: 'none' as const,
      touchAction: 'pan-y' as const,
    };

    return (
      <>
        <div style={overlayStyle} onClick={() => onClose(id)}></div>
        <div
          style={popupStyle}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          <div style={swipeContainerStyle}>
            {links.imgUrl && (
              <img
                src={links.imgUrl}
                alt={t(title)}
                style={imageStyle}
                onClick={handleLinkClick}
              />
            )}
            <div style={contentStyle}>
              {title && <h3>{t(title)}</h3>}
              {content && <div dangerouslySetInnerHTML={{ __html: t(content) }} />}
            </div>
          </div>
          <div style={footerStyle}>
            <button style={buttonStyle} onClick={() => onNeverShow(id)}>{t('다시 보지 않기')}</button>
            <button style={{...buttonStyle, textDecoration: 'none'}} onClick={() => onClose(id)}>{t('닫기')}</button>
          </div>

          {totalCount > 1 && (
            <div style={paginationStyle}>
              {Array.from({ length: totalCount }).map((_, index) => (
                <div
                  key={index}
                  style={dotStyle(index === currentIndex)}
                  onClick={() => {
                    if (index < currentIndex && onPrev) {
                      // 현재보다 이전 인덱스로 이동할 때는 onPrev를 여러 번 호출
                      for (let i = 0; i < currentIndex - index; i++) {
                        onPrev();
                      }
                    } else if (index > currentIndex && onNext) {
                      // 현재보다 이후 인덱스로 이동할 때는 onNext를 여러 번 호출
                      for (let i = 0; i < index - currentIndex; i++) {
                        onNext();
                      }
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </>
    );
  };
  // 팝업 데이터의 인터페이스 정의
  interface PopupData {
    id: string;
    title: string;
    content: string;
    links: any;
  }
  // 팝업 데이터 가져오기 함수
  const fetchPopupData = async (dispatch: any): Promise<PopupData[]> => {
    try {
      // API에서 팝업 데이터 가져오기
      await dispatch(fetchPopupPosts() as any);

      // API에서 데이터를 가져오므로 여기서는 빈 배열 반환
      // Redux store에서 실제 데이터를 활용할 것임
      return [];
    } catch (error) {
      console.error('팝업 데이터 가져오기 오류:', error);
      return [];
    }
  };
// ------------------------------------------------------------------ 팝업 관련 내용 끝



// ------------------------------------------------------------------ 콘솔 메시지 출력 관련 내용 시작
  const printWithcookieMessage = () => {
    const styles = [
      'color: #ffffff',
      'background-color: #4361ee',
      'font-size: 18px',
      'font-weight: bold',
      'padding: 10px 15px',
      'border-radius: 5px',
      'text-shadow: 1px 1px 2px #000',
      'margin: 20px 0'
    ].join(';');

    console.log('%c Withcookie 서비스를 활용하여 만들어진 사이트입니다. 수정, 신규제작 관련 문의는 withcookie.com 으로 해주세요. ', styles);
  };
// ------------------------------------------------------------------ 콘솔 메시지 출력 관련 내용 끝



// ------------------------------------------------------------------ 정의된 데이터 해석 시작
  // WithcookieData 인터페이스 정의
  interface WithcookieData {
    skin?: {
      menuJsonNonUser?: {
        preview_data?: any;
        global_menus?: any;
        main_menus?: any;
        saved_projects?: any;
      };
      theme?: {
        favicon_ico_url?: string;
        favicon16x_url?: string;
        favicon32x_url?: string;
      };
      company?: {
        id?: number;
        business_number?: string;
        companySettingJson?: string | object;
      };
    };
  }
  interface ComponentData {
    id: string;
    type: string;
    content?: any;
    style?: Record<string, any>;
    data?: Record<string, any>;
    parentId?: string;
    order?: number;
    size?: {
      width?: string | number;
      height?: string | number;
    };
    position?: {
      x?: number;
      y?: number;
    };
    componentProps?: Record<string, any>;
  }
  interface MenuData {
    id: string;
    path: string;
    name: string;
    type?: string;
    components?: string[];
    is_logged?: boolean;
    is_not_logged?: boolean;
    is_business_only?: boolean;
  }
  interface PreviewData {
    components: ComponentData[];
    menus: MenuData[];
    layoutInfo: {
      selectedLayout: string;
      layoutProps: Record<string, any>;
    };
    timestamp?: string;
  }
// ------------------------------------------------------------------ 정의된 데이터 해석 끝



// ------------------------------------------------------------------ 기타 유틸리티 시작
  const errorToString = (err: any): string => {
    // 객체를 문자열로 변환하는 유틸리티 함수
    if (err === null || err === undefined) {
      return '';
    }
    if (typeof err === 'string') {
      return err;
    }
    if (typeof err === 'object') {
      try {
        if (err.message) {
          return String(err.message);
        }
        return JSON.stringify(err);
      } catch (e) {
        return '객체를 문자열로 변환할 수 없음';
      }
    }
    return String(err);
  };
// ------------------------------------------------------------------ 기타 유틸리티 끝



// ------------------------------------------------------------------ 프리뷰 작동 위한 것들 시작
  // 경로에서 가변 변수 추출하는 유틸리티 함수
  const extractPathVariables = (patternPath: string, actualPath: string) => {
    const patternParts = patternPath.split('/').filter(Boolean);
    const actualParts = actualPath.split('/').filter(Boolean);

    // 경로 부분 개수가 맞지 않으면 일치하지 않음
    if (patternParts.length !== actualParts.length) {
      return null;
    }

    const variables: Record<string, string> = {};

    for (let i = 0; i < patternParts.length; i++) {
      const patternPart = patternParts[i];
      const actualPart = actualParts[i];

      if (patternPart.startsWith(':')) {
        // 변수 부분 - 변수명과 값 추출
        const varName = patternPart.substring(1); // ':' 제거
        variables[varName] = actualPart;
      } else if (patternPart !== actualPart) {
        // 일반 경로 부분 - 정확히 일치해야 함
        return null;
      }
    }

    return variables;
  };
  // 미리보기 메뉴 라우트 컴포넌트
  const PreviewMenuRoute = ({ cartPage = false, paymentSuccessPage = false, paymentFailPage = false, productDetailPage = false }) => {
    const { menuPath, restPath = '' } = useParams<{ menuPath: string; restPath: string }>();
    const { id: productId } = useParams<{ id: string }>(); // 상품 ID를 상위 레벨에서 추출
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    // URL 쿼리 파라미터에서 디바이스 타입 가져오기
    const deviceType = searchParams.get('device') || 'pc';

    // 회사 사용 상태 가져오기
    const companyIsUse = useSelector(selectCompanyIsUse);
    const reduxPopups = useSelector(selectPopups) as PopupData[] || [];
    // const popupsLoading = useSelector(selectPopupsLoading);
    const apiWithcookieData = useSelector(selectWithcookieData) as WithcookieData || {};
    const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
    const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
    const userInfo = useSelector(selectUserInfo);
    const isBusiness = userInfo?.isBusiness || false;

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [previewData, setPreviewData] = useState<PreviewData | null>(null);
    const [componentsToRender, setComponentsToRender] = useState<ComponentData[]>([]);
    const [pathVariables, setPathVariables] = useState<Record<string, string>>({});
    const [showPopups, setShowPopups] = useState<any[]>([]);

    // 상태 참조를 위한 ref 추가
    const previewDataRef = useRef<PreviewData | null>(null);

    // 팝업 패치 한 번만 실행하기 위한 플래그 ref 추가
    const popupFetchedRef = useRef(false);

    // 현재 표시 중인 팝업 인덱스 상태 추가 - 선언 위치 이동
    const [currentPopupIndex, setCurrentPopupIndex] = useState<number>(0);

    // 브라우저 뒤로가기/앞으로가기 감지 및 처리
    useEffect(() => {
      const handleLocationChange = (event: PopStateEvent) => {
        // window.location.reload();
      };

      // popstate 이벤트는 브라우저의 뒤로가기/앞으로가기 버튼을 누를 때 발생합니다
      window.addEventListener('popstate', handleLocationChange);

      return () => {
        window.removeEventListener('popstate', handleLocationChange);
      };
    }, []);
    // 상태가 업데이트될 때마다 ref도 업데이트
    useEffect(() => {
      previewDataRef.current = previewData;
    }, [previewData]);
    // 로그인 상태일 때 장바구니 데이터 로드
    useEffect(() => {
      if (isUserLoggedIn || isAdminLoggedIn) {
        dispatch(fetchCartItems() as any);
      }
    }, [isUserLoggedIn, isAdminLoggedIn, dispatch]);



    // 다중 렌더링 방지를 위한 안전한 에러 설정 함수
    const setErrorSafely = (errorMessage: string | Error) => {
      setError(typeof errorMessage === 'string' ? errorMessage : errorMessage.message);
    };

    // 최대 시도 횟수 및 카운터 설정
    const MAX_RETRIES = 3;
    const retryCountRef = useRef<number>(0);

    useEffect(() => {
      // state-stop 페이지에서는 리디렉션 중단
      if (location.pathname === '/state-stop') {
        return;
      }

      if (companyIsUse === false) {
        // 리디렉션 횟수 체크 (무한 루프 방지)
        const redirectCount = sessionStorage.getItem('preview_menu_redirect') || '0';
        const count = parseInt(redirectCount, 10);

        // 10초 내 3회 이상 리디렉션이 발생하면 무한 루프로 간주하고 중단
        if (count >= 3) {
          console.warn('PreviewMenu: 너무 많은 리디렉션이 발생했습니다. 추가 리디렉션을 중단합니다.');
          return;
        }

        // 리디렉션 횟수 증가
        sessionStorage.setItem('preview_menu_redirect', (count + 1).toString());

        // 10초 후 리디렉션 횟수 초기화
        setTimeout(() => {
          sessionStorage.removeItem('preview_menu_redirect');
        }, 10000);

        navigate('/state-stop');
      }
    }, [companyIsUse, location.pathname, navigate]);

    useEffect(() => {
      // state-stop 페이지에서는 모든 처리 중단
      if (location.pathname === '/state-stop') {
        setIsLoading(false);
        setError(null);
        setComponentsToRender([]);
        return;
      }

      // 로그인 상태 초기화 (안정성 개선)
      // 앱이 처음 로드되었을 때 로컬 스토리지와 Redux 상태 동기화
      const initLoginState = () => {
        try {
          // 로컬 스토리지에서 마지막 로그인 상태 확인
          const savedLoginState = localStorage.getItem('preview_login_state');
          if (savedLoginState) {
            const { isUserLoggedIn: savedUserState, isAdminLoggedIn: savedAdminState, timestamp } = JSON.parse(savedLoginState);

            // 현재 시간과 저장된 시간 차이 계산 (5분 이내)
            const now = new Date().getTime();
            const diff = now - timestamp;
            const isRecent = diff < 5 * 60 * 1000; // 5분 이내


          }
        } catch (error) {
          console.error('Login state init error:', error);
        }
      };

      // 로그인 상태 초기화 실행
      initLoginState();

      const loadPreviewData = async () => {
        try {
          // API 호출 시 재시도 카운터 초기화
          retryCountRef.current = 0;

          // API 호출 타임아웃 설정
          const timeoutId = setTimeout(() => {
            if (isLoading) {
              console.error('[미리보기] 데이터 로드 타임아웃');
              setErrorSafely('데이터를 불러오는데 시간이 너무 오래 걸립니다. 새로고침 후 다시 시도하세요.');
              setIsLoading(false);
            }
          }, 10000); // 10초 타임아웃

          let initialDataLoaded = false;

          // 최우선 작업: withcookie 데이터 로드 (check_current API 엔드포인트에서 데이터 가져옴)
          try {
            let currentApiData = apiWithcookieData;
            
            // API 데이터가 없을 때만 호출하여 중복 방지
            if (!apiWithcookieData || Object.keys(apiWithcookieData).length === 0) {
              const result = await dispatch(fetchWithcookieData() as any);

              // Redux action의 payload에서 데이터 추출
              if (result?.payload) {
                currentApiData = result.payload;
              } else if (result) {
                // fetchWithcookieData가 직접 데이터를 반환하는 경우
                currentApiData = result;
              }
            }

            // favicon_ico_url이 있으면 파비콘 업데이트
            if (currentApiData?.skin?.theme?.favicon_ico_url) {
              // 기존 favicon 링크 요소 찾기
              const existingFavicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;

              if (existingFavicon) {
                // 기존 favicon이 있으면 href 속성만 변경
                existingFavicon.href = currentApiData.skin.theme.favicon_ico_url;
              } else {
                // 기존 favicon이 없으면 새로 생성하여 추가
                const newFavicon = document.createElement("link");
                newFavicon.rel = "icon";
                newFavicon.href = currentApiData.skin.theme.favicon_ico_url;
                document.head.appendChild(newFavicon);
              }
            }

            // favicon16x_url이 있으면 16x16 파비콘 업데이트
            if (currentApiData?.skin?.theme?.favicon16x_url) {
              // 기존 16x16 favicon 링크 요소 찾기
              const existingFavicon16 = document.querySelector("link[rel='icon'][sizes='16x16']") as HTMLLinkElement;

              if (existingFavicon16) {
                existingFavicon16.href = currentApiData.skin.theme.favicon16x_url;
              } else {
                // 새로운 파비콘 요소를 생성하여 속성을 설정합니다
                const newFavicon16 = document.createElement("link");
                newFavicon16.rel = "icon";
                newFavicon16.href = currentApiData.skin.theme.favicon16x_url;
                newFavicon16.setAttribute('sizes', '16x16');
                newFavicon16.type = "image/png";
                document.head.appendChild(newFavicon16);
              }
            }

            // favicon32x_url이 있으면 32x32 파비콘 업데이트
            if (currentApiData?.skin?.theme?.favicon32x_url) {
              // 기존 32x32 favicon 링크 요소 찾기
              const existingFavicon32 = document.querySelector("link[rel='icon'][sizes='32x32']") as HTMLLinkElement;

              if (existingFavicon32) {
                existingFavicon32.href = currentApiData.skin.theme.favicon32x_url;
              } else {
                // 새로운 파비콘 요소를 생성하여 속성을 설정합니다
                const newFavicon32 = document.createElement("link");
                newFavicon32.rel = "icon";
                newFavicon32.href = currentApiData.skin.theme.favicon32x_url;
                newFavicon32.setAttribute('sizes', '32x32');
                newFavicon32.type = "image/png";
                document.head.appendChild(newFavicon32);
              }
            }

            // 사이트 검증 메타 태그 업데이트
            const skinData = currentApiData?.skin as any;

            if (skinData?.siteVerificationGoogle || skinData?.siteVerificationNaver) {
              const { updateSiteVerificationMetaTags } = await import('./utils/metaTagUtils');
              updateSiteVerificationMetaTags(
                skinData.siteVerificationGoogle,
                skinData.siteVerificationNaver
              );
            }

          } catch (apiError) {
            console.error('[미리보기] API 호출 오류:', apiError);
            // API 호출 실패 시에만 재시도 카운터 증가
            retryCountRef.current++;

            // 최대 시도 횟수를 초과했는지 확인
            if (retryCountRef.current >= MAX_RETRIES) {
              console.warn('[미리보기] 최대 시도 횟수 초과, 로컬 스토리지 데이터 확인 시도');
              // 최대 재시도를 초과해도 로컬 데이터를 시도해봄
            }
          }

          // API 응답에서 preview_data 직접 추출 시도
          if (apiWithcookieData?.skin?.menuJsonNonUser?.preview_data) {
            try {
              // preview_data 파싱
              const previewDataContent = apiWithcookieData.skin.menuJsonNonUser.preview_data;
              const previewData = typeof previewDataContent === 'string'
                ? JSON.parse(previewDataContent)
                : previewDataContent;

              // 🔍 DEBUG: Raw component data from API

              // LOGIN 컴포넌트 상세 확인
              const loginComponents = previewData.components.filter((c: any) => c.type === 'LOGIN');
              if (loginComponents.length > 0) {
              }

              // 로컬 저장소에 미리보기 데이터 업데이트
              localStorage.setItem('previewData', JSON.stringify(previewData));
              localStorage.setItem('previewPath', '/');
              localStorage.setItem('previewTimestamp', new Date().toISOString());

              // 사용할 데이터로 설정
              setPreviewData(previewData);

              // 현재 상태의 previewData 불러오기
              const currentData = await new Promise<PreviewData>((resolve) => {
                setTimeout(() => {
                  resolve(previewDataRef.current as PreviewData);
                }, 50);
              });

              // 경로 및 메뉴 처리 코드로 계속 진행
              processRouteAndMenu(currentData);
              initialDataLoaded = true;

              // API 데이터로 UI를 렌더링했으므로 로딩 상태 해제
              setIsLoading(false);
              clearTimeout(timeoutId);
              return;
            } catch (parseError) {
              console.error('[미리보기] API preview_data 파싱 오류:', parseError);
              // 파싱 오류 시 계속 진행하여 로컬 스토리지 데이터 로드 시도
            }
          }

          // API에서 데이터를 가져오지 못한 경우, 로컬 스토리지에서 미리보기 데이터 확인
          const previewDataString = localStorage.getItem('previewData');

          // 로컬 스토리지에 데이터가 있으면 사용
          if (previewDataString && !initialDataLoaded) {
            try {
              // 로컬 스토리지에 데이터가 있으면 바로 사용
              const data: PreviewData = JSON.parse(previewDataString);

              // 🔍 DEBUG: Raw component data when loaded

              // 데이터 검증
              if (!data || !Array.isArray(data.components) || !Array.isArray(data.menus)) {
                console.error('[미리보기] 유효하지 않은 데이터 구조:', data);
                setErrorSafely('유효하지 않은 미리보기 데이터입니다.');
                setIsLoading(false);
                clearTimeout(timeoutId);
                return;
              }

              // 메뉴 데이터가 비어있으면 API 데이터에서 메뉴를 가져와서 업데이트
              if (data.menus.length === 0 && apiWithcookieData?.skin?.menuJsonNonUser) {
                try {
                  const globalMenus = apiWithcookieData.skin.menuJsonNonUser.global_menus
                    ? (typeof apiWithcookieData.skin.menuJsonNonUser.global_menus === 'string'
                      ? JSON.parse(apiWithcookieData.skin.menuJsonNonUser.global_menus)
                      : apiWithcookieData.skin.menuJsonNonUser.global_menus)
                    : [];

                  const mainMenus = apiWithcookieData.skin.menuJsonNonUser.main_menus
                    ? (typeof apiWithcookieData.skin.menuJsonNonUser.main_menus === 'string'
                      ? JSON.parse(apiWithcookieData.skin.menuJsonNonUser.main_menus)
                      : apiWithcookieData.skin.menuJsonNonUser.main_menus)
                    : [];

                  if (globalMenus.length > 0 || mainMenus.length > 0) {
                    data.menus = [
                      ...globalMenus.map((menu: any) => ({ ...menu, type: 'GLOBAL' })),
                      ...mainMenus.map((menu: any) => ({ ...menu, type: 'MAIN' }))
                    ];

                    // 업데이트된 데이터를 localStorage에 다시 저장
                    localStorage.setItem('previewData', JSON.stringify(data));
                  }
                } catch (menuParseError) {
                  console.error('[Preview.tsx] 메뉴 데이터 보강 중 오류:', menuParseError);
                }
              }

              // 데이터 저장 및 로딩 완료
              setPreviewData(data);

              // 현재 상태의 previewData 불러오기
              const currentData = await new Promise<PreviewData>((resolve) => {
                setTimeout(() => {
                  resolve(previewDataRef.current as PreviewData);
                }, 50);
              });

              // 경로 및 메뉴 처리 코드로 계속 진행
              processRouteAndMenu(currentData);
              initialDataLoaded = true;

              // 로컬 데이터로 UI를 렌더링했으므로 로딩 상태 해제
              setIsLoading(false);
              clearTimeout(timeoutId);
              return;
            } catch (error) {
              console.error('[미리보기] 로컬 스토리지 previewData 파싱 오류:', error);
            }
          }

          // 회사 상태가 false인 경우 여기서 데이터 로드를 중단
          if (companyIsUse === false) {
            setIsLoading(false);
            clearTimeout(timeoutId);
            return;
          }

          // 어디에도 데이터가 없는 경우 오류 표시
          if (!initialDataLoaded) {
            console.error('[미리보기] API와 로컬 스토리지 모두에 데이터가 없음');
            setErrorSafely('미리보기 데이터를 찾을 수 없습니다. 먼저 에디터에서 컴포넌트를 추가해주세요.');
            setIsLoading(false);
          }

          clearTimeout(timeoutId);
          return;
        } catch (error) {
          console.error('[미리보기] 데이터 로드 오류:', error);
          if (typeof error === 'object' && error !== null) {
            console.error('[미리보기] 오류 키:', Object.keys(error));
          }
          // 오류 처리: 알 수 없는 타입의 오류도 문자열로 변환
          const errorMessage = typeof error === 'string'
            ? error
            : error instanceof Error
              ? error.message
              : '알 수 없는 오류가 발생했습니다.';

          // 초기 데이터가 로드되지 않은 경우에만 오류 표시
          if (!previewDataRef.current) {
            setErrorSafely(errorMessage);
            setIsLoading(false);
          } else {
            console.error('[미리보기] API 업데이트 중 오류 발생, 로컬 데이터 유지');
          }
        }
      };

      // 경로 및 메뉴 처리를 위한 공통 함수
      const processRouteAndMenu = (currentData: PreviewData) => {
        // 장바구니, 결제 성공/실패 페이지인 경우 추가 처리 없이 빈 컴포넌트 목록 (레이아웃은 필요)
        if (cartPage || paymentSuccessPage || paymentFailPage) {
          setComponentsToRender([]);
          setIsLoading(false);
          return;
        }

        // 현재 경로 구성
        const fullPath = location.pathname;

        // 시스템에서 허용하는 특수 경로들 (메뉴에 설정되지 않아도 접근 가능한 경로)
        const allowedSystemPaths = [
          '/',
          '/preview',  // 🔧 프리뷰 경로 허용 추가
          '/cart',
          '/payment/success',
          '/payment/fail',
          '/payment/payletter/return',
          '/payment-success',
          '/payment-fail',
          '/state-stop',
          '/toss-test'
        ];

        // 특수 경로 패턴 체크 (정규식으로 체크)
        const allowedPatterns = [
          /^\/products\/\d+$/, // /products/{숫자} 패턴
          /^\/preview\/.*$/, // /preview/{anything} 패턴
          /^\/edit.*$/, // /edit{anything} 패턴
        ];

        // companyId 기반 finapp/gpc 패턴 추가
        const companyId = apiWithcookieData?.skin?.company?.id;

        // companyId에 따라 허용되는 앱 패턴 추가
        if (companyId && [21, 24, 26].includes(companyId)) {
          // finapp 허용 회사
          allowedPatterns.push(/^\/finapp\/.*$/);
        } else if (companyId && [27, 28].includes(companyId)) {
          // gpc 허용 회사
          allowedPatterns.push(/^\/gpc\/.*$/);
        }
        // companyId가 없거나 알 수 없는 경우에는 finapp/gpc 접근 불허

        // 현재 경로가 허용된 시스템 경로인지 체크
        const isAllowedSystemPath = allowedSystemPaths.includes(fullPath) ||
          allowedPatterns.some(pattern => pattern.test(fullPath));

        // 메뉴 매칭 및 가변 변수 추출
        let matchedMenu = null;
        let extractedVariables = {};


        // 1. 정확한 경로 매칭 시도 - 모든 메뉴 타입 포함
        matchedMenu = currentData.menus.find(menu => menu.path === fullPath);

        // 2. 정확한 매칭이 없으면 와일드카드 매칭 시도
        if (!matchedMenu) {
          for (const menu of currentData.menus) {
            // ** 와일드카드 처리 (예: /shopping/**)
            if (menu.path && menu.path.includes('/**')) {
              const basePath = menu.path.replace('/**', '');
              if (fullPath === basePath || fullPath.startsWith(basePath + '/')) {
                matchedMenu = menu;
                break;
              }
            }
          }
        }

        // 3. 와일드카드 매칭도 없으면 가변 경로 매칭 시도
        if (!matchedMenu) {
          for (const menu of currentData.menus) {
            if (menu.path.includes('/:')) {
              // 가변 변수가 포함된 경로
              const variables = extractPathVariables(menu.path, fullPath);
              if (variables) {
                matchedMenu = menu;
                extractedVariables = variables;

                // 회원가입 페이지 경로 패턴 확인 - signup 경로와 추천인 코드
                const pathParts = menu.path.split('/').filter(Boolean);
                if (pathParts.length >= 1 && pathParts[0] === 'signup') {
                  // URL 형태가 /signup 또는 /signup/:referralCode 패턴인 경우


                  // matchedMenu를 설정하여 정상적인 메뉴로 처리하도록 함
                  matchedMenu = menu;

                  // 이 메뉴에 컴포넌트가 없다면, 대신 SignUpComponent를 사용할 컴포넌트로 설정
                  if (!matchedMenu.components?.length) {
                    const signupComponent: ComponentData = {
                      id: 'signup-component',
                      type: 'SIGNUP',
                      content: {
                        referralCode: variables['referralCode'] // 변수 정보 전달
                      },
                      style: { margin: '20px auto' },
                      data: {
                        referralCode: variables['referralCode'] // 변수 정보 data에도 전달
                      },
                      componentProps: {
                        varFields: {
                          var01: { show: false, label: "", type: "input", options: [], required: false, content: "" },
                          var02: { show: false, label: "", type: "input", options: [], required: false, content: "" },
                          var03: { show: false, label: "", type: "input", options: [], required: false, content: "" }
                        }
                      }
                    };

                    // 렌더링할 컴포넌트로 signup 컴포넌트 설정
                    setComponentsToRender([signupComponent]);
                    setPathVariables(variables); // 경로 변수 저장
                    setIsLoading(false);
                    return;
                  }
                }

                break;
              }
            }
          }
        }

        // 3. 정확한 경로 체크: /signup 직접 처리
        if (!matchedMenu && fullPath === '/signup') {

          // signup 메뉴 찾기 시도
          const signupMenu = currentData.menus.find(menu =>
            menu.path === '/signup' ||
            menu.path.startsWith('/signup/') ||
            (menu.path.includes('/:') && menu.path.split('/').filter(Boolean)[0] === 'signup')
          );

          if (signupMenu) {
            matchedMenu = signupMenu;

            // 메뉴를 찾았지만 컴포넌트가 없는 경우 기본 컴포넌트 제공
            if (!signupMenu.components?.length) {
              const signupComponent: ComponentData = {
                id: 'signup-component',
                type: 'SIGNUP',
                content: {},
                style: { margin: '20px auto' },
                data: {},
                componentProps: {
                  varFields: {
                    var01: { show: false, label: "", type: "input", options: [], required: false, content: "" },
                    var02: { show: false, label: "", type: "input", options: [], required: false, content: "" },
                    var03: { show: false, label: "", type: "input", options: [], required: false, content: "" }
                  }
                }
              };

              // 렌더링할 컴포넌트로 signup 컴포넌트 설정
              setComponentsToRender([signupComponent]);
              setIsLoading(false);
              return;
            }
          } else {
            // 회원가입 메뉴가 없으면 기본 컴포넌트 생성
            const signupComponent: ComponentData = {
              id: 'signup-component',
              type: 'SIGNUP',
              content: {},
              style: { margin: '20px auto' },
              data: {},
              componentProps: {
                varFields: {
                  var01: { show: false, label: "", type: "input", options: [], required: false, content: "" },
                  var02: { show: false, label: "", type: "input", options: [], required: false, content: "" },
                  var03: { show: false, label: "", type: "input", options: [], required: false, content: "" }
                }
              }
            };

            setComponentsToRender([signupComponent]);
            setIsLoading(false);
            return;
          }
        }

        // 추출된 경로 변수 저장
        setPathVariables(extractedVariables);

        // 4. 로그인 상태 확인 및 리다이렉트 처리
        // 주의: 이 부분은 이제 useEffect에서 처리되므로 여기에서는 제거합니다.
        // 메뉴 정보는 저장해 둡니다.
        if (matchedMenu) {
          // 메뉴 정보 저장 (로그인 제한 메뉴 여부 확인용)
          if (matchedMenu.is_logged || matchedMenu.is_not_logged) {
            // 해당 메뉴는 로그인 제한이 있는 메뉴입니다.
            // 실제 리다이렉트 처리는 별도의 useEffect에서 처리됩니다.
          }
        }

        // 렌더링할 컴포넌트 결정
        if (matchedMenu) {
          // 메뉴를 찾았을 경우
          if (matchedMenu.components?.length) {
            // 메뉴에 할당된 컴포넌트가 있는 경우
            const menuComponents = matchedMenu.components
              .map(id => currentData.components.find(c => c.id === id))
              .filter((comp): comp is ComponentData => comp !== undefined)
              .map((comp, index) => ({
                ...comp,
                order: comp.order !== undefined ? comp.order : index * 10
              }))
              .sort((a, b) => (a.order || 0) - (b.order || 0));



            // 반드시 로딩 상태 종료 - 컴포넌트 유무와 관계없이
            setComponentsToRender(menuComponents);
            setIsLoading(false);
            return;
          } else {
            // 메뉴는 있지만 컴포넌트가 없는 경우 - 빈 화면 표시
            setComponentsToRender([]);
            setIsLoading(false);
            return;
          }
        } else {
          // 메뉴를 못 찾은 경우
          if (isAllowedSystemPath) {
            // 허용된 시스템 경로인 경우 루트 컴포넌트 사용 (기존 동작 유지)
            const rootComponents = currentData.components
              .filter(comp => !comp.parentId)
              .map((comp, index) => ({
                ...comp,
                order: comp.order !== undefined ? comp.order : index * 10
              }))
              .sort((a, b) => (a.order || 0) - (b.order || 0));

            setComponentsToRender(rootComponents);
            setIsLoading(false);
          } else {
            // 허용되지 않은 경로인 경우 루트(/)로 리다이렉트

            // finapp/gpc 간의 잘못된 접근에 대한 특별한 처리
            const companyId = apiWithcookieData?.skin?.company?.id;
            if (fullPath.startsWith('/finapp') && companyId && [27, 28].includes(companyId)) {
              // gpc 사용 회사가 finapp에 접근한 경우 gpc로 리다이렉트
              navigate('/gpc');
              return;
            } else if (fullPath.startsWith('/gpc') && companyId && [21, 24, 26].includes(companyId)) {
              // finapp 사용 회사가 gpc에 접근한 경우 finapp으로 리다이렉트
              navigate('/finapp');
              return;
            }

            // 그 외의 경우 루트로 리다이렉트
            navigate('/');
            return;
          }
        }
      };

      loadPreviewData();
    }, [menuPath, restPath, location.pathname, dispatch, companyIsUse, navigate, cartPage, paymentSuccessPage, paymentFailPage]);

    // 로그인 상태 변경 시 접근 권한 다시 확인하는 useEffect 추가
    useEffect(() => {
      // 로직 실행 조건:
      // 1. 로딩 중이 아니고
      // 2. previewData가 있고
      // 3. 모든 필요한 로그인 상태 정보가 로드된 상태여야 함
      if (!isLoading && previewData && apiWithcookieData) {
        // 페이지 로드 시 로그인 상태 다시 확인
        const checkLoginState = async () => {
          try {


            // 로그인 상태는 이미 로드된 상태이므로 중복 호출 방지
            // await dispatch(fetchWithcookieData() as any);

            // 약간의 지연 후 처리 (로그인 상태가 업데이트될 시간 확보)
            setTimeout(() => {

              // 로그인 상태를 로컬 스토리지에 저장 (안전장치)
              localStorage.setItem('preview_login_state', JSON.stringify({
                isUserLoggedIn,
                isAdminLoggedIn,
                timestamp: new Date().getTime()
              }));

              // 현재 경로와 일치하는 메뉴 찾기
              const fullPath = location.pathname;
              const matchedMenu = previewData.menus.find(menu => menu.path === fullPath);

              // 가변 경로 매칭도 시도
              let variablePathMenu = null;
              if (!matchedMenu) {
                for (const menu of previewData.menus) {
                  if (menu.path.includes('/:')) {
                    const variables = extractPathVariables(menu.path, fullPath);
                    if (variables) {
                      variablePathMenu = menu;
                      break;
                    }
                  }
                }
              }

              // 사용할 메뉴 (정확한 경로 또는 가변 경로)
              const currentMenu = matchedMenu || variablePathMenu;

              // 로그인 상태 확인 및 리다이렉트 처리
              if (currentMenu) {


                // 로그인 시에만 표시(is_logged)가 설정된 메뉴에 비로그인 상태로 접근한 경우
                if (currentMenu.is_logged && !isUserLoggedIn && !isAdminLoggedIn) {

                  navigate('/');
                  return;
                }

                // 로그아웃 시에만 표시(is_not_logged)가 설정된 메뉴에 로그인 상태로 접근한 경우
                if (currentMenu.is_not_logged && (isUserLoggedIn || isAdminLoggedIn)) {

                  navigate('/');
                  return;
                }

                // 사업자 전용 메뉴(is_business_only)에 비사업자가 접근한 경우
                if (currentMenu.is_business_only && (!isUserLoggedIn || !isBusiness)) {

                  navigate('/');
                  return;
                }


              } else {

              }
            }, 300); // 충분한 지연 시간 사용
          } catch (error) {
            console.error('로그인 상태 확인 중 오류:', error);
          }
        };

        checkLoginState();
      }
    }, [location.pathname, previewData, dispatch, isUserLoggedIn, isAdminLoggedIn, navigate]);




    // ------------------------------------------------------------------ 팝업 데이터 로드 및 표시 시작
      // 팝업 데이터를 가져오고 표시하는 단일 useEffect
      useEffect(() => {
        const loadAndDisplayPopups = async () => {
          try {
            // 이미 한 번 실행됐으면 중단
            if (popupFetchedRef.current) {
              return;
            }



            // check_current가 먼저 로드되었는지 확인 - 중복 호출 방지
            if (!apiWithcookieData || !Object.keys(apiWithcookieData).length) {
              // 이미 상위에서 fetchWithcookieData가 호출되므로 중복 방지
              await new Promise(resolve => setTimeout(resolve, 200));
            }

            // 팝업 데이터가 없으면 가져오기
            if (Array.isArray(reduxPopups) && reduxPopups.length === 0) {
              // 요청 상태 표시
              popupFetchedRef.current = true;
              // API에서 팝업 데이터 가져오기
              await dispatch(fetchPopupPosts() as any);
            } else {
              popupFetchedRef.current = true;
            }
          } catch (error) {
            console.error('[팝업] 데이터 로드 오류:', error);
          }
        };

        // DOM이 완전히 로드된 후 실행하도록 변경
        if (document.readyState === 'complete') {
          // 이미 DOM이 로드되었으면 바로 실행
          loadAndDisplayPopups();
        } else {
          // DOM 로드가 완료될 때까지 기다렸다가 실행
          window.addEventListener('load', loadAndDisplayPopups);

          // 컴포넌트 언마운트 시 이벤트 리스너 제거
          return () => {
            window.removeEventListener('load', loadAndDisplayPopups);
          };
        }
      }, [dispatch]); // 의존성 최소화하여 무한 루프 방지

      // 팝업 데이터 표시를 위한 별도의 useEffect 추가 - 데이터 로드와 표시 로직 분리
      useEffect(() => {
        // 이미 redux에 팝업 데이터가 있는 경우에만 실행
        if (Array.isArray(reduxPopups) && reduxPopups.length > 0) {

          try {
            // 숨긴 팝업 ID 목록 가져오기
            const hiddenPopups = JSON.parse(localStorage.getItem('hiddenPopups') || '[]');

            // 데이터 형식 변환 및 숨김 처리된 팝업 필터링
            const formattedPopups = reduxPopups
              .filter(popup => {
                const isValid = popup && popup.title && popup.content;
                const isHidden = hiddenPopups.includes(popup.id);

                // 추가 디버깅: 팝업 데이터 상세 출력

                return isValid && !isHidden;
              })
              .map(popup => ({
                ...popup,
                links: {
                  imgUrl: popup.links?.imgUrl || '',
                  textAlignment: popup.links?.textAlignment || 'center',
                  textJustify: popup.links?.textJustify || 'center',
                  textColor: popup.links?.textColor || '#000000',
                  backgroundColor: popup.links?.backgroundColor || '#ffffff',
                  popupPadding: popup.links?.popupPadding || '20',
                  link: popup.links?.link || '',
                }
              }));

            // 유효한 팝업 데이터가 있는 경우에만 상태 업데이트 및 표시
            if (formattedPopups.length > 0) {
              setShowPopups(formattedPopups);
              // 현재 팝업 인덱스 초기화
              setCurrentPopupIndex(0);
            } else {

            }
          } catch (error) {
            console.error('[팝업] 데이터 처리 오류:', error);
          }
        }
      }, [reduxPopups]); // reduxPopups가 변경될 때만 실행
      // 다음 팝업으로 이동
      const nextPopup = () => {
        if (currentPopupIndex < showPopups.length - 1) {
          setCurrentPopupIndex(currentPopupIndex + 1);
        }
      };
      // 이전 팝업으로 이동
      const prevPopup = () => {
        if (currentPopupIndex > 0) {
          setCurrentPopupIndex(currentPopupIndex - 1);
        }
      };
      // 팝업 닫기 함수
      const closePopup = (popupId: string) => {
        // 현재 표시된 팝업에서 특정 팝업만 제거
        const updatedPopups = showPopups.filter(popup => popup.id !== popupId);

        if (updatedPopups.length > 0) {
          // 남은 팝업이 있으면 업데이트
          setShowPopups(updatedPopups);
          // 인덱스 조정
          if (currentPopupIndex >= updatedPopups.length) {
            setCurrentPopupIndex(updatedPopups.length - 1);
          }
        } else {
          // 남은 팝업이 없으면 모두 닫기
          setShowPopups([]);
          setCurrentPopupIndex(0);
        }
      };
      // 다시 보지 않기 함수
      const neverShowPopup = (popupId: string) => {
        // 현재 저장된 팝업 ID 목록 가져오기
        const hiddenPopups = JSON.parse(localStorage.getItem('hiddenPopups') || '[]');
        // 현재 팝업 ID 추가
        if (!hiddenPopups.includes(popupId)) {
          hiddenPopups.push(popupId);
        }
        // 업데이트된 목록 저장
        localStorage.setItem('hiddenPopups', JSON.stringify(hiddenPopups));

        // 현재 표시된 팝업 제거하고 다음 팝업으로 이동
        const updatedPopups = showPopups.filter(popup => popup.id !== popupId);

        if (updatedPopups.length > 0) {
          // 남은 팝업이 있으면 업데이트
          setShowPopups(updatedPopups);
          // 인덱스 조정
          if (currentPopupIndex >= updatedPopups.length) {
            setCurrentPopupIndex(updatedPopups.length - 1);
          }
        } else {
          // 남은 팝업이 없으면 모두 닫기
          setShowPopups([]);
          setCurrentPopupIndex(0);
        }
      };
    // ------------------------------------------------------------------ 팝업 데이터 로드 및 표시 끝
    // 로딩 중 표시
    if (isLoading) {
      return (
        <>
          {showPopups.length > 0 && (
            <Popup
              id={showPopups[currentPopupIndex].id}
              title={showPopups[currentPopupIndex].title}
              content={showPopups[currentPopupIndex].content}
              links={showPopups[currentPopupIndex].links}
              onClose={closePopup}
              onNeverShow={neverShowPopup}
              currentIndex={currentPopupIndex}
              totalCount={showPopups.length}
              onNext={nextPopup}
              onPrev={prevPopup}
            />
          )}
          <div className="preview-loading">
            {/* Withcookie 서비스를 활용하여 만들어진 사이트입니다. 수정, 신규제작 관련 문의는 withcookie.com 으로 해주세요. */}
            <div className="loading-spinner"></div>
          </div>
        </>
      );
    }

    // 오류 표시
    if (error) {

      return (
        <>
          {showPopups.length > 0 && (
            <Popup
              id={showPopups[currentPopupIndex].id}
              title={showPopups[currentPopupIndex].title}
              content={showPopups[currentPopupIndex].content}
              links={showPopups[currentPopupIndex].links}
              onClose={closePopup}
              onNeverShow={neverShowPopup}
              currentIndex={currentPopupIndex}
              totalCount={showPopups.length}
              onNext={nextPopup}
              onPrev={prevPopup}
            />
          )}
          <div className="preview-error">
            {/* Withcookie 서비스를 활용하여 만들어진 사이트입니다. 수정, 신규제작 관련 문의는 withcookie.com 으로 해주세요. */}
            <h3>{errorToString(error)}</h3>
            <p>사이트 설정을 확인해주세요.</p>
            <button onClick={() => window.close()} className="close-preview-button">닫기</button>
            {/* 디버깅용 버튼 추가 */}
            <button
              onClick={() => {
                localStorage.setItem('hiddenPopups', '[]');
                alert('숨겨진 팝업 목록을 초기화했습니다. 페이지를 새로고침합니다.');
                window.location.reload();
              }}
              style={{ marginTop: '10px', backgroundColor: '#ff6b6b', color: 'white' }}
            >
              팝업 초기화 (디버깅용)
            </button>
          </div>
        </>
      );
    }

    // 데이터 없음 표시
    if (!previewData) {
      return (
        <>
          {showPopups.length > 0 && (
            <Popup
              id={showPopups[currentPopupIndex].id}
              title={showPopups[currentPopupIndex].title}
              content={showPopups[currentPopupIndex].content}
              links={showPopups[currentPopupIndex].links}
              onClose={closePopup}
              onNeverShow={neverShowPopup}
              currentIndex={currentPopupIndex}
              totalCount={showPopups.length}
              onNext={nextPopup}
              onPrev={prevPopup}
            />
          )}
          <div className="preview-empty">
            {/* Withcookie 서비스를 활용하여 만들어진 사이트입니다. 수정, 신규제작 관련 문의는 withcookie.com 으로 해주세요. */}
            <p>미리보기할 내용이 없습니다.</p>
            <p>에디터에서 컴포넌트를 추가한 후 다시 시도하세요.</p>
            <button onClick={() => window.close()} className="close-preview-button">닫기</button>
            {/* 디버깅용 버튼 추가 */}
            <button
              onClick={() => {
                localStorage.setItem('hiddenPopups', '[]');
                alert('숨겨진 팝업 목록을 초기화했습니다. 페이지를 새로고침합니다.');
                window.location.reload();
              }}
              style={{ marginTop: '10px', backgroundColor: '#ff6b6b', color: 'white' }}
            >
              팝업 초기화 (디버깅용)
            </button>
          </div>
        </>
      );
    }




    // ------------------------------------------------------------------ 레이아웃 적용 및 컴포넌트 렌더링 시작
      // 레이아웃 적용 및 컴포넌트 렌더링
      const selectedLayoutId = previewData.layoutInfo?.selectedLayout || '';
      // 레이아웃 정보 찾기 (없으면 기본 레이아웃 사용)
      const LayoutComponent = getLayout(selectedLayoutId);

      // 레이아웃 속성 설정
      const defaultLayoutProps = previewData.layoutInfo?.layoutProps || {};

      // 실제 렌더링에 사용할 props 구성

      const layoutProps = {
        ...defaultLayoutProps,
        previewData: previewData,
        withcookieData: apiWithcookieData,
        globalMenus: previewData.menus.filter(menu => menu.type === 'GLOBAL'),
        mainMenus: previewData.menus.filter(menu => menu.type !== 'GLOBAL'),
        // 프리뷰에서도 에디터와 동일한 메뉴 데이터 구조 전달
        editorData: {
          ...previewData,
          menus: previewData.menus // editorSlice의 menus와 동일한 구조
        },
        viewMode: deviceType, // 디바이스 타입 전달
        isMobile: deviceType === 'mobile',
        isTablet: deviceType === 'tablet',
        isDesktop: deviceType === 'pc'
      };

    // ------------------------------------------------------------------ 레이아웃 적용 및 컴포넌트 렌더링 끝



    // ------------------------------------------------------------------ 렌더링 하기 위해 넘기기 위한 데이터 정리 시작
      // 이 부분은 위에서 이미 처리되었으므로 제거합니다.
    // ------------------------------------------------------------------ 렌더링 하기 위해 넘기기 위한 데이터 정리 끝

    // 장바구니 페이지인 경우 CartComponent 렌더링
    if (cartPage) {
      // 장바구니 컴포넌트 찾기
      const cartComponent = previewData?.components.find(comp =>
        comp.type === 'CART' || comp.type === 'CartComponent'
      );

      return (
        <>
          {showPopups.length > 0 && (
            <Popup
              id={showPopups[currentPopupIndex].id}
              title={showPopups[currentPopupIndex].title}
              content={showPopups[currentPopupIndex].content}
              links={showPopups[currentPopupIndex].links}
              onClose={closePopup}
              onNeverShow={neverShowPopup}
              currentIndex={currentPopupIndex}
              totalCount={showPopups.length}
              onNext={nextPopup}
              onPrev={prevPopup}
            />
          )}
          <LayoutComponent {...{
            ...layoutProps,
            containerStyle: {
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              width: '100%'
            },
            forceDeviceType: deviceType
          }}>
            {/* Withcookie 서비스를 활용하여 만들어진 사이트입니다. 수정, 신규제작 관련 문의는 withcookie.com 으로 해주세요. */}
            <div className="preview-content" style={{
              flex: '1 0 auto',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: deviceType === 'mobile' ? '40px 0 20px 0' : (deviceType === 'tablet' ? '20px 0' : '0')
            }}>
              <CartComponent
                {...cartComponent}
                componentProps={cartComponent?.componentProps || {}}
                style={cartComponent?.style || {}}
              />
            </div>
          </LayoutComponent>
        </>
      );
    }
    // 결제 성공 페이지인 경우 PaymentSuccess 렌더링
    if (paymentSuccessPage) {
      // 결제 성공 컴포넌트 찾기
      const paymentSuccessComponent = previewData?.components.find(comp =>
        comp.type === 'PAYMENT_SUCCESS' || comp.type === 'PaymentSuccess'
      );

      return (
        <>
          {showPopups.length > 0 && (
            <Popup
              id={showPopups[currentPopupIndex].id}
              title={showPopups[currentPopupIndex].title}
              content={showPopups[currentPopupIndex].content}
              links={showPopups[currentPopupIndex].links}
              onClose={closePopup}
              onNeverShow={neverShowPopup}
              currentIndex={currentPopupIndex}
              totalCount={showPopups.length}
              onNext={nextPopup}
              onPrev={prevPopup}
            />
          )}
          <LayoutComponent {...{
            ...layoutProps,
            containerStyle: {
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              width: '100%'
            },
            forceDeviceType: deviceType
          }}>
            {/* Withcookie 서비스를 활용하여 만들어진 사이트입니다. 수정, 신규제작 관련 문의는 withcookie.com 으로 해주세요. */}
            <div className="preview-content" style={{
              flex: '1 0 auto',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: deviceType === 'mobile' ? '40px 0 20px 0' : (deviceType === 'tablet' ? '20px 0' : '0')
            }}>
              <PaymentSuccess
                {...paymentSuccessComponent}
                componentProps={paymentSuccessComponent?.componentProps || {}}
                style={paymentSuccessComponent?.style || {}}
              />
            </div>
          </LayoutComponent>
        </>
      );
    }
    // 결제 실패 페이지인 경우 PaymentFail 렌더링
    if (paymentFailPage) {
      // 결제 실패 컴포넌트 찾기
      const paymentFailComponent = previewData?.components.find(comp =>
        comp.type === 'PAYMENT_FAIL' || comp.type === 'PaymentFail'
      );

      return (
        <>
          {showPopups.length > 0 && (
            <Popup
              id={showPopups[currentPopupIndex].id}
              title={showPopups[currentPopupIndex].title}
              content={showPopups[currentPopupIndex].content}
              links={showPopups[currentPopupIndex].links}
              onClose={closePopup}
              onNeverShow={neverShowPopup}
              currentIndex={currentPopupIndex}
              totalCount={showPopups.length}
              onNext={nextPopup}
              onPrev={prevPopup}
            />
          )}
          <LayoutComponent {...{
            ...layoutProps,
            containerStyle: {
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              width: '100%'
            },
            forceDeviceType: deviceType
          }}>
            {/* Withcookie 서비스를 활용하여 만들어진 사이트입니다. 수정, 신규제작 관련 문의는 withcookie.com 으로 해주세요. */}
            <div className="preview-content" style={{
              flex: '1 0 auto',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: deviceType === 'mobile' ? '40px 0 20px 0' : (deviceType === 'tablet' ? '20px 0' : '0')
            }}>
              <PaymentFail
                {...paymentFailComponent}
                componentProps={paymentFailComponent?.componentProps || {}}
                style={paymentFailComponent?.style || {}}
              />
            </div>
          </LayoutComponent>
        </>
      );
    }

    // 상품 상세 페이지인 경우 ProductDetail 컴포넌트 렌더링
    if (productDetailPage) {
      // 상품 상세 컴포넌트 찾기 및 중복 확인
      const allProductDetailComponents = previewData?.components.filter(comp =>
        comp.type === 'PRODUCT_DETAIL' || comp.type === 'ProductDetail'
      ) || [];

      // 중복된 ProductDetail 컴포넌트가 있는지 확인
      if (allProductDetailComponents.length > 1) {
        console.warn(`[Preview] 경고: ${allProductDetailComponents.length}개의 ProductDetail 컴포넌트가 발견되었습니다. 첫 번째 것만 사용합니다.`);
      }

      const productDetailComponent = allProductDetailComponents[0];

      // URL에서 상품 ID 가져오기 - 이미 상위에서 추출함
      // const { id } = useParams<{ id: string }>();

      return (
        <>
          {showPopups.length > 0 && (
            <Popup
              id={showPopups[currentPopupIndex].id}
              title={showPopups[currentPopupIndex].title}
              content={showPopups[currentPopupIndex].content}
              links={showPopups[currentPopupIndex].links}
              onClose={closePopup}
              onNeverShow={neverShowPopup}
              currentIndex={currentPopupIndex}
              totalCount={showPopups.length}
              onNext={nextPopup}
              onPrev={prevPopup}
            />
          )}
          <LayoutComponent {...{
            ...layoutProps,
            containerStyle: {
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              width: '100%'
            },
            forceDeviceType: deviceType
          }}>
            {/* Withcookie 서비스를 활용하여 만들어진 사이트입니다. 수정, 신규제작 관련 문의는 withcookie.com 으로 해주세요. */}
            <div className="preview-content" style={{
              flex: '1 0 auto',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: deviceType === 'mobile' ? '40px 0 20px 0' : (deviceType === 'tablet' ? '20px 0' : '0')
            }}>
              <ProductDetailComponent
                {...productDetailComponent}
                componentProps={{
                  ...(productDetailComponent?.componentProps || {}),
                  productId: productId // URL에서 가져온 상품 ID 전달 (상위에서 추출한 값 사용)
                }}
                style={productDetailComponent?.style || {}}
              />
            </div>
          </LayoutComponent>
        </>
      );
    }

    // 일반 페이지인 경우 componentsToRender 사용하여 렌더링
    return (
      <>
        {showPopups.length > 0 && (
          <Popup
            id={showPopups[currentPopupIndex].id}
            title={showPopups[currentPopupIndex].title}
            content={showPopups[currentPopupIndex].content}
            links={showPopups[currentPopupIndex].links}
            onClose={closePopup}
            onNeverShow={neverShowPopup}
            currentIndex={currentPopupIndex}
            totalCount={showPopups.length}
            onNext={nextPopup}
            onPrev={prevPopup}
          />
        )}

        <LayoutComponent {...{
          ...layoutProps,
          containerStyle: {
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            width: '100%',
            overflow: 'auto' // 스크롤 가능하도록 설정
          },
          // 프리뷰에서 레이아웃 스타일 강제 적용
          forceDeviceType: deviceType
        }}>
          {/* Withcookie 서비스를 활용하여 만들어진 사이트입니다. 수정, 신규제작 관련 문의는 withcookie.com 으로 해주세요. */}
          <div className="preview-content" style={{
            flex: '1 0 auto',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: deviceType === 'mobile' ? '40px 0 20px 0' : (deviceType === 'tablet' ? '20px 0' : '0'),
            overflow: 'auto' // 스크롤 가능하도록 설정
          }}>
            {componentsToRender.length > 0
            ?
            (
              // 컴포넌트가 있는 경우
              componentsToRender.map(component => {
                // ProductDetail 컴포넌트가 일반 페이지에 포함되어 있는지 확인
                if (component.type === 'PRODUCT_DETAIL' || component.type === 'ProductDetail') {
                  console.warn(`[Preview] 경고: ProductDetail 컴포넌트가 일반 페이지에 포함되어 있습니다. 이는 /products/:id 경로에서만 사용되어야 합니다.`);
                }
                return (
                  <ComponentRendererWithProps
                    key={component.id}
                    component={component}
                    allComponents={previewData.components}
                    pathVariables={pathVariables || {}}
                  />
                );
              })
            )
            :
            (
              // 컴포넌트가 없는 경우 (빈 화면)
              <div style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '20px',
                fontSize: '16px',
                color: '#666',
                textAlign: 'center',
                margin: 'auto'
              }}>
                표시할 내용이 없습니다.
              </div>
            )}
          </div>
        </LayoutComponent>
      </>
    );


  };
  const Preview = ({ cartPage = false, paymentSuccessPage = false, paymentFailPage = false, productDetailPage = false }) => {
    console.error('🚨 Preview 컴포넌트 렌더링됨 - ERROR 레벨:', new Date().toISOString());

    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const companyIsUse = useSelector(selectCompanyIsUse);
    const withcookieData = useSelector(selectWithcookieData); // Hook을 최상위에서 호출
    const [searchParams] = useSearchParams();

    // URL 쿼리 파라미터에서 디바이스 타입과 경로 가져오기
    const deviceType = searchParams.get('device') || 'pc';
    const customPath = searchParams.get('path') || '';

    // 모든 useEffect를 early return 이전에 호출
    useEffect(() => {
      // 콘솔에 강조된 메시지 출력
      printWithcookieMessage();

      // state-stop 페이지에서는 API 호출 및 리디렉션 중단
      if (location.pathname === '/state-stop') return;

      // 최우선 작업: 초기 데이터 로드
      dispatch(fetchWithcookieData() as any);


      // 회사 상태가 false인 경우 state-stop 페이지로 리디렉션
      if (companyIsUse === false) {
        navigate('/state-stop');
      }

      // 디바이스 타입에 따른 스타일 적용
      document.body.classList.remove('device-pc', 'device-tablet', 'device-mobile');
    }, [dispatch, companyIsUse, location.pathname, navigate, deviceType, customPath]);

    // 🔥 finapp 경로 감지 및 처리 - lazy loading 적용
    if (location.pathname.startsWith('/finapp')) {
      // withcookieData에서 companyId 확인 (이미 최상위에서 가져옴)
      const companyId = withcookieData?.skin?.company?.id;

      // withcookieData가 로드되지 않은 경우 로딩 대기
      if (!withcookieData || Object.keys(withcookieData).length === 0) {
        return <div className="preview-loading"><div className="loading-spinner"></div></div>;
      }

      // companyId 기반 접근 제한 (finapp 허용: 21, 24, 26)
      if (companyId && ![21, 24, 26].includes(companyId)) {
        // finapp 접근 권한이 없는 companyId인 경우
        if ([27, 28].includes(companyId)) {
          // gpc 사용 회사라면 gpc로 리다이렉트
          navigate('/gpc');
          return null;
        } else {
          // 알 수 없는 companyId라면 루트로 리다이렉트
          navigate('/');
          return null;
        }
      }

      // LayoutRegistry에서 finapp-component를 lazy loading으로 가져오기
      const FinappComponent = getLayout('finapp-component');
      // 상대 경로로 변환하여 finapp 내부 라우팅이 동작하도록 함
      const relativePath = location.pathname.replace('/finapp', '') || '/';
      return <FinappComponent relativePath={relativePath} />;
    }

    // 🔥 gpc 경로 감지 및 처리 - lazy loading 적용
    if (location.pathname.startsWith('/gpc')) {
      // withcookieData에서 companyId 확인 (이미 최상위에서 가져옴)
      const companyId = withcookieData?.skin?.company?.id;

      // withcookieData가 로드되지 않은 경우 로딩 대기
      if (!withcookieData || Object.keys(withcookieData).length === 0) {
        return <div className="preview-loading"><div className="loading-spinner"></div></div>;
      }

      // companyId 기반 접근 제한 (gpc 허용: 27, 28, 30)
      if (companyId && ![27, 28, 30].includes(companyId)) {
        // gpc 접근 권한이 없는 companyId인 경우
        if ([21, 24, 26].includes(companyId)) {
          // finapp 사용 회사라면 finapp으로 리다이렉트
          navigate('/finapp');
          return null;
        } else {
          // 알 수 없는 companyId라면 루트로 리다이렉트
          navigate('/');
          return null;
        }
      }

      // LayoutRegistry에서 gpc-component를 lazy loading으로 가져오기
      const GpcComponent = getLayout('gpc-component');
      // 상대 경로로 변환하여 gpc 내부 라우팅이 동작하도록 함
      const relativePath = location.pathname.replace('/gpc', '') || '/';
      return <GpcComponent relativePath={relativePath} />;
    }

    // 🔥 midas 경로 감지 및 처리 - lazy loading 적용
    if (location.pathname.startsWith('/midas')) {
      // LayoutRegistry에서 midas-component를 lazy loading으로 가져오기
      const MidasComponent = getLayout('midas-component');
      // 상대 경로로 변환하여 midas 내부 라우팅이 동작하도록 함
      const relativePath = location.pathname.replace('/midas', '') || '/';
      return <MidasComponent relativePath={relativePath} />;
    }

    return (
      <div className={`preview-container device-${deviceType}`}>
      {/* <div className={`preview-container`}> */}
        {/* Withcookie 서비스를 활용하여 만들어진 사이트입니다. 수정, 신규제작 관련 문의는 withcookie.com 으로 해주세요. */}
        <div className="preview-content">

          <Routes>
            <Route
              path="/"
              element={
                <PreviewMenuRoute
                  cartPage={cartPage}
                  paymentSuccessPage={paymentSuccessPage}
                  paymentFailPage={paymentFailPage}
                  productDetailPage={productDetailPage}
                />
              }
            />
            <Route
              path="/:menuPath/*"
              element={
                <PreviewMenuRoute
                  cartPage={cartPage}
                  paymentSuccessPage={paymentSuccessPage}
                  paymentFailPage={paymentFailPage}
                  productDetailPage={productDetailPage}
                />
              }
            />
          </Routes>
        </div>
      </div>
    );
  };
// ------------------------------------------------------------------ 프리뷰 작동 위한 것들 끝


export default Preview;
